import {
  Component,
  OnInit,
  Inject,
  ViewChildren,
  QueryList,
  ElementRef,
} from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UserService } from 'src/app/services/user.service';

@Component({
  selector: 'app-otp-verification',
  templateUrl: './otp-verification.component.html',
  styleUrls: ['./otp-verification.component.scss'],
})
export class OtpVerificationComponent implements OnInit {
  @ViewChildren('otpInput') inputs!: QueryList<ElementRef>;

  otpForm: FormGroup;
  timer: number = 60;
  canResend: boolean = false;
  interval: any;
  loading: boolean = false;
  errorMessage: string = '';

  constructor(
    private fb: FormBuilder,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private userService: UserService,
    private dialogRef: MatDialogRef<OtpVerificationComponent>,
  ) {
    this.otpForm = this.fb.group({
      digits: this.fb.array(
        new Array(6)
          .fill('')
          .map(() => [
            '',
            [Validators.required, Validators.pattern('^[0-9]$')],
          ]),
      ),
    });
  }

  get digits() {
    return this.otpForm.get('digits') as FormArray;
  }

  ngOnInit(): void {
    this.startTimer();
  }

  onInput(index: number) {
    const control = this.digits.at(index);
    const value = control.value;

    if (value && !/^\d$/.test(value)) {
      control.setValue('');
      return;
    }

    const inputsArray = this.inputs.toArray();
    if (value && index < 5) {
      inputsArray[index + 1].nativeElement.focus();
    }

    this.errorMessage = '';
  }

  onKeyDown(event: KeyboardEvent, index: number) {
    const inputsArray = this.inputs.toArray();
    if (event.ctrlKey || event.metaKey) {
      return;
    }

    const isNumber = /^[0-9]$/.test(event.key);
    const isControlKey = [
      'Backspace',
      'Tab',
      'Enter',
      'ArrowLeft',
      'ArrowRight',
      'Delete',
    ].includes(event.key);

    if (!isNumber && !isControlKey) {
      event.preventDefault();
      return;
    }

    if (event.key === 'Backspace') {
      if (!this.digits.at(index).value && index > 0) {
        inputsArray[index - 1].nativeElement.focus();
      }
    } else if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault();
      inputsArray[index - 1].nativeElement.focus();
    } else if (event.key === 'ArrowRight' && index < 5) {
      event.preventDefault();
      inputsArray[index + 1].nativeElement.focus();
    }
  }

  handleFocus(index: number) {
    const input = this.inputs.toArray()[index].nativeElement;
    setTimeout(() => {
      const len = input.value.length;
      input.setSelectionRange(len, len);
    }, 0);
  }

  onPaste(event: ClipboardEvent) {
    event.preventDefault();
    event.stopImmediatePropagation();

    const clipboardData = event.clipboardData || (window as any).clipboardData;
    let pastedText = clipboardData.getData('text').replace(/\D/g, '');

    if (pastedText.length >= 6) {
      pastedText = pastedText.slice(0, 6);

      this.digits.clear();

      for (let i = 0; i < 6; i++) {
        this.digits.push(
          this.fb.control(pastedText[i], [
            Validators.required,
            Validators.pattern('^[0-9]$'),
          ]),
        );
      }

      setTimeout(() => {
        this.inputs.last.nativeElement.focus();
        this.otpForm.updateValueAndValidity();
      });

      this.errorMessage = '';
    } else {
      this.errorMessage = 'El código debe tener al menos 6 números';
    }
  }

  verify() {
    if (this.otpForm.invalid || this.loading) return;
    this.loading = true;
    this.errorMessage = '';
    const code = this.digits.value.join('');

    this.userService.validateSMS(code).subscribe({
      next: (res) => {
        this.loading = false;
        if (res.success) {
          this.dialogRef.close('verified');
        } else {
          this.errorMessage = res.message || 'Código incorrecto o expirado';
        }
      },
      error: (err) => {
        this.loading = false;
        this.errorMessage =
          err.error?.message || 'Hubo un problema al validar el código';
      },
    });
  }

  startTimer() {
    this.timer = 60;
    this.canResend = false;
    clearInterval(this.interval);
    this.interval = setInterval(() => {
      if (this.timer > 0) this.timer--;
      else {
        this.canResend = true;
        clearInterval(this.interval);
      }
    }, 1000);
  }

  resendCode() {
    if (this.canResend) {
      this.userService.sendSMS(this.data.phone).subscribe();
      this.startTimer();
    }
  }

  close() {
    this.dialogRef.close();
  }
}
